import { getErrorMessage } from "@/utils/errorHelpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { getFileUrl, ordersAPI } from "@/services/api";
import { toast } from "sonner";
import { formatCurrency, formatDate } from "@/utils/constants";
import { CheckCircle, ChevronLeft, ChevronRight, FileDown, FileSearch, Filter, Search, Trash2, Upload } from "lucide-react";
import { ADMIN_RANKS, hasAdminRank, hasFullAdminRank, isCashAccount } from "@/utils/adminRanks";

const ITEMS_PER_PAGE = 10;
const makeFallbackKey = (prefix = "row") => `${prefix}-${Math.random().toString(36).slice(2)}`;

export default function OrdersPage({ user }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofDetails, setProofDetails] = useState({
    reference: "",
    payment_channel: "",
    payer_name: user?.full_name || "",
    payer_account: user?.id || "",
    paid_at: "",
    amount_declared_mc: "",
    notes: "",
  });
  const [uploading, setUploading] = useState(false);
  const [validatingGroup, setValidatingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("date");
  const [orderType, setOrderType] = useState("product");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const canValidateOrders = hasAdminRank(user, ADMIN_RANKS.C);
  const canUploadProof = isCashAccount(user);
  const canApproveDeletion = hasFullAdminRank(user);

  const loadOrders = useCallback(async () => {
    try {
      const response = await ordersAPI.list({ order_type: orderType });
      setOrders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du chargement des commandes"));
    } finally {
      setLoading(false);
    }
  }, [orderType]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const grouped = useMemo(() => {
    const safeOrders = Array.isArray(orders) ? orders : [];
    return safeOrders.map((batch) => {
      const isActivation = orderType === "activation";
      const batchOrders = (batch.orders_details || []).filter((order) => isActivation ? order.product_id === "PROD_ACTIVATION" : order.product_id !== "PROD_ACTIVATION");
      const allOrdersValidated = batchOrders.length > 0 && batchOrders.every((order) => order.status === "validated" || order.payment?.validated);
      const orderWithProof = batchOrders.find((order) => order.payment_proof_path || order.payment?.proof_path);
      const proofPath = batch.payment?.proof_path || (isActivation ? batch.payment_activation?.proof_path : batch.payment_product?.proof_path) || batch.proof_path || orderWithProof?.payment_proof_path || orderWithProof?.payment?.proof_path;

      return {
        key: batch.batch_id || batch.id || makeFallbackKey("batch"),
        batch_id: batch.batch_id || batch.id || "-",
        buyer_id: batch.buyer_id || "-",
        month: batch.month,
        created_at: batch.created_at,
        orders: batchOrders,
        product_orders: isActivation ? [] : batchOrders,
        order_type: orderType,
        total_mc: batch.total_amount_mc || 0,
        has_proof: !!proofPath,
        proof_path: proofPath,
        payment_metadata: batch.payment?.metadata || batch.payment_product?.metadata || orderWithProof?.payment?.metadata,
        business_signature: batch.business_signature || batchOrders.find((order) => order.business_signature)?.business_signature,
        payment_validated: !!batch.payment?.validated || !!batch.payment_activation?.validated || ["validated", "completed"].includes(batch.status) || allOrdersValidated,
        validated_by:
          batch.payment?.validated_by_name ||
          batch.payment?.validated_admin_name ||
          batch.payment?.admin_name ||
          batch.payment?.validated_by ||
          batch.validated_by_name ||
          batch.validated_by,
        validated_at: batch.payment?.validated_at || batch.validated_at,
        product_ids: batchOrders.map((order) => order.product_id).filter(Boolean),
        order_count: batchOrders.length,
        first_order_id: batchOrders?.[0]?.id || null,
        deletion_request: batch.deletion_request || null,
      };
    });
  }, [orders, orderType]);

  const filtered = useMemo(() => {
    let result = [...grouped];

    if (filterStatus === "pending") result = result.filter((group) => !group.has_proof);
    else if (filterStatus === "paid") result = result.filter((group) => group.has_proof);

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      result = result.filter((group) =>
        String(group.buyer_id || "").toLowerCase().includes(term) ||
        group.product_ids.some((id) => String(id || "").toLowerCase().includes(term)) ||
        group.first_order_id?.toLowerCase().includes(term) ||
        group.batch_id?.toLowerCase().includes(term)
      );
    }

    if (sortBy === "date") result.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sortBy === "buyer") result.sort((a, b) => String(a.buyer_id || "").localeCompare(String(b.buyer_id || "")));
    else if (sortBy === "amount") result.sort((a, b) => b.total_mc - a.total_mc);
    else if (sortBy === "product") result.sort((a, b) => (a.product_ids[0] || "").localeCompare(b.product_ids[0] || ""));

    return result;
  }, [grouped, filterStatus, search, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleUploadGroupProof = async (event) => {
    event.preventDefault();
    if (!proofFile || !selectedGroup) return;
    setUploading(true);
    try {
      await ordersAPI.uploadPaymentProof(selectedGroup.batch_id, proofFile, selectedGroup.order_type, proofDetails);
      toast.success(`Preuve uploadee pour ${selectedGroup.batch_id}`);
      setSelectedGroup(null);
      setProofFile(null);
      setProofDetails({
        reference: "",
        payment_channel: "",
        payer_name: user?.full_name || "",
        payer_account: user?.id || "",
        paid_at: "",
        amount_declared_mc: "",
        notes: "",
      });
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de l'upload"));
    } finally {
      setUploading(false);
    }
  };

  const handleValidateGroup = async (group) => {
    if (!group.has_proof) {
      toast.error("Cette commande n'a pas de preuve de paiement. Contactez la caisse.");
      return;
    }
    const orderIds = (group.product_orders || []).map((order) => order.id).filter(Boolean);
    if (orderIds.length === 0) {
      toast.error("Aucune commande produit a valider dans ce lot");
      return;
    }

    setValidatingGroup(group.batch_id);
    try {
      await Promise.all(orderIds.map((orderId) => ordersAPI.validateOrder(orderId, true)));
      toast.success("Commande validée");
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de la validation"));
    } finally {
      setValidatingGroup(null);
    }
  };

  const handleDeleteGroup = async (group) => {
    const confirmation = group.has_proof
      ? "Cette commande contient une preuve de paiement. Envoyer une demande de suppression a un administrateur Full ?"
      : "Supprimer definitivement cette commande sans preuve de paiement ?";
    if (!window.confirm(confirmation)) return;

    setDeletingGroup(group.batch_id);
    try {
      const response = await ordersAPI.deleteBatchFlow(group.batch_id, group.order_type);
      toast.success(response.data?.requires_full_approval ? "Demande de suppression envoyee" : "Commande supprimée");
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de la suppression"));
    } finally {
      setDeletingGroup(null);
    }
  };

  const handleApproveDeletion = async (group) => {
    if (!group.deletion_request || !window.confirm("Approuver et executer cette suppression ?")) return;
    setDeletingGroup(group.batch_id);
    try {
      await ordersAPI.approveDeletionRequest(group.deletion_request.id);
      toast.success("Suppression approuvée et exécutée");
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de l'approbation"));
    } finally {
      setDeletingGroup(null);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <PageShell data-testid="orders-page">
      <PageHeader
        title={user?.role === "admin" ? (orderType === "activation" ? "Commandes d'activation" : "Commandes PAE") : (orderType === "activation" ? "Mes bons d'activation" : "Mes commandes PAE")}
        description={`${filtered.length} groupe(s) filtre(s) sur ${orders.length} commande(s).`}
      />

      <Card>
        <CardContent className="grid gap-3 pt-5 sm:grid-cols-2 lg:grid-cols-[1fr_180px_180px_180px_auto] sm:pt-6">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F8C]" size={17} />
            <Input
              placeholder="Rechercher ID, produit, acheteur..."
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              className="h-11 pl-10"
              data-testid="order-search"
            />
          </div>
          <Select value={orderType} onValueChange={(value) => { setOrderType(value); setPage(1); }}>
            <SelectTrigger className="h-11" data-testid="order-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="product">Commandes PAE</SelectItem>
              <SelectItem value="activation">Bons d'activation</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={(value) => { setFilterStatus(value); setPage(1); }}>
            <SelectTrigger className="h-11" data-testid="order-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="paid">Payees</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(value) => { setSortBy(value); setPage(1); }}>
            <SelectTrigger className="h-11" data-testid="order-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Par date</SelectItem>
              <SelectItem value="buyer">Par acheteur</SelectItem>
              <SelectItem value="amount">Par montant</SelectItem>
              <SelectItem value="product">Par produit</SelectItem>
            </SelectContent>
          </Select>
          <div className="hidden items-center gap-2 text-xs font-semibold text-[#5C625F] lg:flex">
            <Filter size={15} /> Filtres
          </div>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-[#5C625F]">Aucune commande trouvee</CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {paginated.map((group) => (
              <OrderGroupCard
                key={group.key}
                group={group}
                user={user}
                canValidateOrders={canValidateOrders}
                canUploadProof={canUploadProof}
                canApproveDeletion={canApproveDeletion}
                validatingGroup={validatingGroup}
                deletingGroup={deletingGroup}
                selectedGroup={selectedGroup}
                setSelectedGroup={setSelectedGroup}
                setProofFile={setProofFile}
                proofDetails={proofDetails}
                setProofDetails={setProofDetails}
                uploading={uploading}
                handleUploadGroupProof={handleUploadGroupProof}
                handleValidateGroup={handleValidateGroup}
                handleDeleteGroup={handleDeleteGroup}
                handleApproveDeletion={handleApproveDeletion}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2" data-testid="pagination">
              <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} data-testid="page-prev">
                <ChevronLeft size={14} />
              </Button>
              <span className="text-xs text-[#5C625F]">Page {page} / {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} data-testid="page-next">
                <ChevronRight size={14} />
              </Button>
            </div>
          )}
        </>
      )}
    </PageShell>
  );
}

function OrderGroupCard({ group, user, canValidateOrders, canUploadProof, canApproveDeletion, validatingGroup, deletingGroup, selectedGroup, setSelectedGroup, setProofFile, proofDetails, setProofDetails, uploading, handleUploadGroupProof, handleValidateGroup, handleDeleteGroup, handleApproveDeletion }) {
  const setProofField = (key, value) => setProofDetails((prev) => ({ ...prev, [key]: value }));

  return (
    <Card data-testid={`order-group-${group.key}`}>
      <CardContent className="flex flex-col gap-4 pt-5 sm:pt-6 xl:flex-row xl:items-center">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-[#5C625F]">{group.batch_id}</span>
            {group.has_proof ? (
              <Badge className="bg-green-100 text-green-800">Payee</Badge>
            ) : (
              <Badge className="bg-yellow-100 text-yellow-800">En attente</Badge>
            )}
            {group.order_type === "activation" && <Badge className="bg-amber-100 text-amber-800">Activation</Badge>}
            <Badge className="bg-[#F3EFE9] text-[#2A4B46]">{group.order_count} article(s)</Badge>
          </div>

          {(group.business_signature || group.payment_metadata) && (
            <div className="mt-3 grid gap-2 text-xs text-[#5C625F] md:grid-cols-2">
              {group.business_signature && (
                <div className="rounded-lg bg-[#F9F7F4] p-2">
                  <p className="font-semibold text-[#2A4B46]">Signature: {group.business_signature.signer_name || group.buyer_id}</p>
                  <p>Compte: {group.business_signature.buyer_account_id || group.buyer_id}</p>
                </div>
              )}
              {group.payment_metadata && (
                <div className="rounded-lg bg-[#F9F7F4] p-2">
                  <p className="font-semibold text-[#2A4B46]">Ref paiement: {group.payment_metadata.reference || "-"}</p>
                  <p>{group.payment_metadata.payment_channel || "Canal non precisé"} - {group.payment_metadata.paid_at || "date non precisée"}</p>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 grid gap-1 text-xs text-[#5C625F] sm:grid-cols-2 lg:grid-cols-4">
            <span>Acheteur: {group.buyer_id}</span>
            <span>{group.order_type === "activation" ? "Activation du compte" : `Produits: ${group.product_ids.length}`}</span>
            <span>{group.month}</span>
            {group.payment_validated && group.validated_by && (
              <span data-testid={`order-validated-by-${group.key}`}>
                Validée par: {group.validated_by}{group.validated_at ? ` le ${formatDate(group.validated_at)}` : ""}
              </span>
            )}
          </div>

          {group.orders.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {group.orders.map((order) => (
                <span key={order.id} className="rounded-lg bg-[#F9F7F4] px-2 py-1 font-mono text-[10px] text-[#5C625F]" data-testid={`order-id-${order.id}`}>
                  {order.id}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-[#E6E4E0] pt-4 xl:w-auto xl:border-t-0 xl:pt-0">
          <div className="text-left text-xl font-bold text-[#D4A373] xl:text-right">{formatCurrency(group.total_mc)}</div>
          <div className="flex flex-wrap gap-2 xl:justify-end">
            {group.first_order_id && (
              <a href={ordersAPI.getReceiptUrl(group.first_order_id)} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center justify-center rounded-lg bg-blue-50 px-3 text-xs font-semibold text-blue-700 hover:bg-blue-100" data-testid={`receipt-${group.key}`}>
                <FileDown size={14} className="mr-1" /> Bon de commande
              </a>
            )}

            {group.has_proof && group.proof_path && (canValidateOrders || canUploadProof) && (
              <a href={getFileUrl(group.proof_path)} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center justify-center rounded-lg bg-amber-50 px-3 text-xs font-semibold text-amber-700 hover:bg-amber-100" data-testid={`payment-proof-${group.key}`}>
                <FileSearch size={14} className="mr-1" /> Preuve
              </a>
            )}

            {canValidateOrders && !group.payment_validated && group.product_orders.length > 0 && (
              <Button size="sm" className="bg-green-700 text-white hover:bg-green-800" onClick={() => handleValidateGroup(group)} disabled={validatingGroup === group.batch_id} data-testid={`validate-order-${group.key}`}>
                <CheckCircle size={14} /> {validatingGroup === group.batch_id ? "Validation..." : "valider"}
              </Button>
            )}

            {canApproveDeletion && group.deletion_request && !group.payment_validated && (
              <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => handleApproveDeletion(group)} disabled={deletingGroup === group.batch_id} data-testid={`approve-order-deletion-${group.key}`}>
                <CheckCircle size={14} /> {deletingGroup === group.batch_id ? "Approbation..." : "Approuver suppression"}
              </Button>
            )}

            {canUploadProof && !group.payment_validated && !group.deletion_request && (
              <Button size="icon" variant="ghost" className="h-9 w-9 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => handleDeleteGroup(group)} disabled={deletingGroup === group.batch_id} title="Supprimer la commande" aria-label="Supprimer la commande" data-testid={`delete-order-${group.key}`}>
                <Trash2 size={15} />
              </Button>
            )}

            {canUploadProof && !group.has_proof && (
              <Dialog onOpenChange={(open) => {
                if (!open) {
                  setSelectedGroup(null);
                  setProofFile(null);
                  setProofDetails({
                    reference: "",
                    payment_channel: "",
                    payer_name: user?.full_name || "",
                    payer_account: user?.id || "",
                    paid_at: "",
                    amount_declared_mc: "",
                    notes: "",
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]" onClick={() => setSelectedGroup(group)} data-testid={`upload-proof-${group.key}`}>
                    <Upload size={14} /> Preuve
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[92dvh] w-[calc(100vw-1.5rem)] overflow-y-auto sm:max-w-lg" data-testid="upload-proof-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-lg font-bold text-[#2A4B46] font-['Outfit']">Uploader la preuve de paiement</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUploadGroupProof} className="space-y-3">
                    <div className="rounded-xl bg-[#F3EFE9] p-3">
                      <p className="text-sm font-semibold text-[#2A4B46]">
                        {selectedGroup?.order_count} commande(s) - Total: {formatCurrency(selectedGroup?.total_mc || 0)}
                      </p>
                      <p className="mt-1 text-xs text-[#5C625F]">{selectedGroup?.order_type === "activation" ? "Activation du compte partenaire" : `Produits: ${selectedGroup?.product_ids?.join(", ")}`}</p>
                    </div>
                    <div>
                      <Label htmlFor="proof_file" className="text-sm">Preuve de paiement (JPEG, PNG ou PDF)</Label>
                      <Input id="proof_file" type="file" data-testid="proof-file-input" onChange={(event) => setProofFile(event.target.files[0])} accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf" required className="mt-1" />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-sm">Reference transaction</Label>
                        <Input value={proofDetails.reference} onChange={(e) => setProofField("reference", e.target.value)} placeholder="M-Pesa, banque, caisse..." className="mt-1" data-testid="proof-reference-input" />
                      </div>
                      <div>
                        <Label className="text-sm">Canal</Label>
                        <Input value={proofDetails.payment_channel} onChange={(e) => setProofField("payment_channel", e.target.value)} placeholder="Mobile Money, banque..." className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-sm">Nom du payeur</Label>
                        <Input value={proofDetails.payer_name} onChange={(e) => setProofField("payer_name", e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-sm">Compte / numéro payeur</Label>
                        <Input value={proofDetails.payer_account} onChange={(e) => setProofField("payer_account", e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-sm">Date du paiement</Label>
                        <Input type="date" value={proofDetails.paid_at} onChange={(e) => setProofField("paid_at", e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-sm">Montant verse (MC)</Label>
                        <Input type="number" step="0.01" min="0" value={proofDetails.amount_declared_mc} onChange={(e) => setProofField("amount_declared_mc", e.target.value)} className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Note</Label>
                      <Textarea value={proofDetails.notes} onChange={(e) => setProofField("notes", e.target.value)} placeholder="Coordonnees utiles, nom agent, precision sur la preuve..." className="mt-1" />
                    </div>
                    <p className="text-xs leading-5 text-[#5C625F]">Cette preuve unique sera appliquee aux commandes du groupe. Le montant declare doit correspondre au total du lot.</p>
                    <Button type="submit" className="w-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]" disabled={uploading || !selectedGroup} data-testid="submit-proof-button">
                      {uploading ? "Upload en cours..." : "Déposer la preuve"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
